import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';
import { MemberAccount } from '../../../models/transactions/member-account';
import { MemberAccountStatusIdEnum } from '../../../models/transactions/enum/member-account-status-id.enum';
import { AccountTypeIdEnum } from '../../../models/enum/shared/account-type-id.enum';
import {
  AccountStatusPrimaryColors,
  AccountStatusSecondaryColors
} from '../../../models/enum/shared/member-account-status-colors';
import { StatusPill } from '../../../models/shared/status-pill';
import { PermissionService } from '../../../services/permission-service';
import { StatusPillComponent, StatusPillSize } from '../../../ui/pills/status-pill.component';
import { buildAccountStatusPill } from '../../../utils/account-status-pill-utils';

interface StatusChangeOption {
  status: MemberAccountStatusIdEnum;
  pill: StatusPill;
  description: string;
}

function getStatusChangeOption(account: MemberAccount, allowStrike: boolean): StatusChangeOption | null {
  if (account.statusId === MemberAccountStatusIdEnum.Pending) {
    return allowStrike
      ? {
          status: MemberAccountStatusIdEnum.Struck,
          pill: buildStatusPill(MemberAccountStatusIdEnum.Struck, $localize`Struck`),
          description: $localize`Cancels an account set up in error. The record is kept, but the account no longer appears on the member’s portal.`
        }
      : null;
  }
  if (account.typeId === AccountTypeIdEnum.MonthlyPension) {
    return null;
  }
  if (account.statusId === MemberAccountStatusIdEnum.Active) {
    return {
      status: MemberAccountStatusIdEnum.Suspended,
      pill: buildStatusPill(MemberAccountStatusIdEnum.Suspended, $localize`Suspended`),
      description:
        account.typeId === AccountTypeIdEnum.VariableBenefit
          ? $localize`Payments instructed from this account are held in the suspended payment table.`
          : $localize`Suspends all of this member’s Defined Contribution accounts. Contributions and transactions carry on as normal.`
    };
  }
  if (account.statusId === MemberAccountStatusIdEnum.Suspended) {
    return {
      status: MemberAccountStatusIdEnum.Active,
      pill: buildStatusPill(MemberAccountStatusIdEnum.Active, $localize`Active`),
      description: $localize`Lift suspension. The account goes back to Active`
    };
  }
  return null;
}

function buildStatusPill(status: MemberAccountStatusIdEnum, name: string): StatusPill {
  return new StatusPill(status, name, AccountStatusPrimaryColors[status], AccountStatusSecondaryColors[status]);
}

@Component({
  selector: 'app-ip-account-status-menu',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, StatusPillComponent],
  templateUrl: './ip-account-status-menu.component.html',
  styleUrls: ['./ip-account-status-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IpAccountStatusMenuComponent implements OnChanges {
  private permissionService = inject(PermissionService);

  @Input({ required: true }) account!: MemberAccount;
  @Input() allowStrike = false;
  @Input() size: StatusPillSize = 'small';
  @Output() statusSelected = new EventEmitter<MemberAccountStatusIdEnum>();

  // TODO: CSS-3941 use the change account status permission once the backend defines it
  public canChangeStatus$ = this.permissionService.permissionGranted$([0]);

  public statusPill!: StatusPill;
  public option: StatusChangeOption | null = null;
  public hasMenu = false;

  public popperOptions = (options: Partial<Options>) => {
    options.modifiers = [...(options.modifiers ?? []), { name: 'offset', options: { offset: [0, 8] } }];
    return options;
  };

  public ngOnChanges(): void {
    this.statusPill = buildAccountStatusPill(this.account.status);
    this.option = getStatusChangeOption(this.account, this.allowStrike);
    const isOpenMonthlyPension =
      this.account.typeId === AccountTypeIdEnum.MonthlyPension &&
      [MemberAccountStatusIdEnum.Active, MemberAccountStatusIdEnum.Suspended].contains(this.account.statusId);
    this.hasMenu = !!this.option || isOpenMonthlyPension;
  }

  public selectStatus(dropdown: NgbDropdown, toggle: HTMLButtonElement, status: MemberAccountStatusIdEnum): void {
    // The host opens a modal on emit, and the modal returns focus to whatever had it.
    // Leaving focus on the option would send it to a hidden element.
    dropdown.close();
    toggle.focus();
    this.statusSelected.emit(status);
  }
}
